import { useEffect, useState } from 'react';
import { ApiError } from '../../../lib/api';
import {
  operatorConsole,
  type PackBuilderGroup,
  type PackBuilderUpdatePatch,
  type PackValidation,
} from '../../../lib/api/operatorConsole';
import { Button } from '../../../components/ui/Button';
import { RangeInput } from '../../../components/ui/RangeInput';
import { PackHealthPanel } from '../items/PackHealthPanel';
import { BuybackField } from '../odds/BuybackField';
import { GroupsTable } from '../odds/GroupsTable';
import { NumberCell } from '../odds/NumberCell';
import {
  SUPPORT_DISABLED_HINT,
  formatMoney,
  formatPercent,
  isStaged,
  isSupportDisabled,
} from '../packBuilderFormat';
import type { PackTabProps } from './packTabProps';

export const ODDS_VALIDATE_DEBOUNCE_MS = 400;

const MIN_RTP = 0.05;
const RTP_STEP = 0.01;
const LOCKED_HINT = 'The pack is live — unpublish it to change price, RTP or buckets.';
const RESTOCKING_HINT =
  'The pack is restocking and managed automatically — price, RTP and buckets are locked.';
const SECTION = 'space-y-3 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4';

function sameGroups(a: PackBuilderGroup[], b: PackBuilderGroup[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function templateGroups(price: number): Promise<PackBuilderGroup[]> {
  return operatorConsole.packBuilder
    .groupsTemplate(price)
    .then((template) => template.map((g) => ({ from: g.from, to: g.to, floor: null })));
}

/**
 * Drafts for price / RTP / buckets with a live validator preview. Nothing is
 * written until Save. Buckets travel in one of two ways (CONTRACT §4.9): a
 * hand-edited ladder goes out as `groups`; "Reset Price Buckets" goes out as
 * `reset_groups: true` with NO `groups`, so the backend rebuilds
 * GroupsTemplate.ladder(price) itself and clears `groups_custom` — the table
 * only shows the template as a preview until then. An untouched ladder is not
 * sent at all and stays "auto" on the backend, which regenerates it on every
 * price change.
 *
 * ROUND-TRIP RULE. A group carries a solver floor
 * (`{"bucket":"100..Infinity","floor":5}` on the backend, `floor` in the API
 * row), edited in the table's Min % column. The draft starts as the server's
 * own `pack.groups` entries and every edit spreads the incoming row, so a
 * field nobody touched travels back verbatim instead of being flattened to
 * null. Floorless rows are produced in exactly one place — `templateGroups`,
 * i.e. a ladder the operator explicitly asked to be rebuilt.
 */
export function OddsTab({ pack, limits, currency, onPackChange, notify }: PackTabProps) {
  // Only a staged pack is editable: enabled and restocking both count as
  // published (CONTRACT §4.8), `disabled` is support-controlled.
  const locked = !isStaged(pack);
  const lockedHint = isSupportDisabled(pack)
    ? SUPPORT_DISABLED_HINT
    : pack.availability === 'restocking'
      ? RESTOCKING_HINT
      : LOCKED_HINT;
  const packCurrency = pack.currency ?? currency;
  const [price, setPrice] = useState(pack.price);
  const [rtp, setRtp] = useState(pack.stated_rtp);
  const [groups, setGroups] = useState<PackBuilderGroup[]>(pack.groups);
  const [groupsTouched, setGroupsTouched] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [preview, setPreview] = useState<PackValidation | null>(null);
  const [validating, setValidating] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [savingBuyback, setSavingBuyback] = useState(false);

  useEffect(() => {
    setPrice(pack.price);
    setRtp(pack.stated_rtp);
    setGroups(pack.groups);
    setGroupsTouched(false);
    setResetRequested(false);
  }, [pack.price, pack.stated_rtp, pack.groups]);

  useEffect(() => {
    let cancelled = false;
    setValidating(true);
    const timer = setTimeout(() => {
      operatorConsole.packBuilder
        .validate(pack.id, { price, stated_rtp: rtp, groups })
        .then((report) => {
          if (cancelled) return;
          setPreview(report);
          setValidating(false);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setValidating(false);
          // Rack::Attack caps POST …/validate at 60/60s per IP; a long slider
          // drag can reach it. Keep the last preview and say so quietly.
          if (err instanceof ApiError && err.status === 429) {
            notify(
              'info',
              'Too many checks in a row — the preview refreshes after your next change.',
            );
            return;
          }
          notify('error', err instanceof Error ? err.message : 'Validation failed');
        });
    }, ODDS_VALIDATE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pack.id, price, rtp, groups, notify]);

  const groupsDirty = groupsTouched && !sameGroups(groups, pack.groups);
  const dirty = price !== pack.price || rtp !== pack.stated_rtp || groupsDirty || resetRequested;

  async function handlePriceCommit(next: number) {
    const rounded = Math.round(next * 100) / 100;
    setPrice(rounded);
    // A hand-edited draft keeps its boundaries. An auto ladder — or a pending
    // reset — follows the price, like the backend will on save.
    if (groupsTouched || (pack.groups_custom && !resetRequested)) return;
    try {
      setGroups(await templateGroups(rounded));
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not build the ladder');
    }
  }

  async function handleReset() {
    setResetting(true);
    try {
      // Preview only: Save sends reset_groups: true and the backend builds the
      // real ladder (CONTRACT §4.9).
      setGroups(await templateGroups(price));
      setGroupsTouched(false);
      setResetRequested(true);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not build the ladder');
    } finally {
      setResetting(false);
    }
  }

  async function handleBuybackSave(share: number | null) {
    setSavingBuyback(true);
    try {
      const updated = await operatorConsole.packBuilder.updatePack(pack.id, {
        buyback_percent: share,
      });
      onPackChange(updated);
      notify('success', share === null ? 'Buyback reset to the default' : 'Buyback saved');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not save the buyback');
    } finally {
      setSavingBuyback(false);
    }
  }

  function handleGroupsChange(next: PackBuilderGroup[]) {
    setGroups(next);
    setGroupsTouched(true);
    // An edit after Reset wins: the edited buckets are what gets saved.
    setResetRequested(false);
  }

  function handleDiscard() {
    setPrice(pack.price);
    setRtp(pack.stated_rtp);
    setGroups(pack.groups);
    setGroupsTouched(false);
    setResetRequested(false);
  }

  async function handleSave() {
    const patch: PackBuilderUpdatePatch = {
      ...(price !== pack.price ? { price } : {}),
      ...(rtp !== pack.stated_rtp ? { stated_rtp: rtp } : {}),
    };
    // Never both: reset_groups asks the backend for its ladder, groups sends ours.
    // `groups` are the server's entries with edited boundaries — floors included
    // (round-trip rule above) — and are omitted entirely while the ladder is untouched.
    if (resetRequested) patch.reset_groups = true;
    else if (groupsDirty) patch.groups = groups;
    setSaving(true);
    try {
      const updated = await operatorConsole.packBuilder.updatePack(pack.id, patch);
      onPackChange(updated);
      notify('success', 'Odds & price saved');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        notify('error', lockedHint);
      } else {
        notify('error', err instanceof Error ? err.message : 'Could not save');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4">
        {locked && (
          <p className="rounded-lg border border-[var(--co-border)] bg-[var(--co-bg)] px-3 py-2 text-[12px] text-[var(--co-muted)]">
            {lockedHint}
          </p>
        )}

        {/* Two rows, not one line of everything: the typed fields sit side by
            side with the computed Target EV pinned to the right edge, and the
            slider — the one control that wants width — gets a row of its own. */}
        <section className={SECTION}>
          <h2 className="text-[13px] font-semibold text-[var(--co-text)]">Price &amp; RTP</h2>

          <div className="flex flex-wrap items-start gap-8">
            <label className="flex flex-col gap-1 text-[11px] text-[var(--co-muted)]">
              {`Price${packCurrency ? ` (${packCurrency})` : ''}`}
              {/* Not clearable, so the cell only ever commits a number here. */}
              <NumberCell
                aria-label="Price"
                value={price}
                min={0.01}
                step="0.01"
                disabled={locked}
                onCommit={(v) => {
                  if (v !== null) void handlePriceCommit(v);
                }}
              />
            </label>

            {/* Saved on its own, and not disabled by `locked`: buyback is the
                sell-back price of a card the player already holds, so a live
                pack keeps it editable. Only support-disabled packs refuse. */}
            <BuybackField
              value={pack.buyback_percent}
              defaultShare={limits.default_buyback_percent}
              disabled={isSupportDisabled(pack)}
              saving={savingBuyback}
              onSave={(share) => void handleBuybackSave(share)}
            />

            <div className="ml-auto rounded-lg border border-[var(--co-border)] bg-[var(--co-bg)] px-3 py-2 text-right">
              <div className="text-[11px] text-[var(--co-muted)]">Target EV</div>
              <div className="font-mono text-[14px] text-[var(--co-text)]">
                {formatMoney(Math.round(price * rtp * 100) / 100, packCurrency)}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--co-muted)]">RTP</span>
            <RangeInput
              aria-label="RTP"
              value={rtp}
              min={MIN_RTP}
              max={limits.max_rtp}
              step={RTP_STEP}
              disabled={locked}
              onChange={setRtp}
              format={(v) => formatPercent(v)}
            />
            {rtp < limits.min_rtp_warn && (
              <p className="text-[11px] text-[var(--co-warning)]">
                Below {formatPercent(limits.min_rtp_warn)} — the validator will warn.
              </p>
            )}
          </div>
        </section>

        <GroupsTable
          groups={groups}
          price={price}
          disabled={locked}
          resetting={resetting}
          onChange={handleGroupsChange}
          onReset={() => void handleReset()}
        />

        <div className="flex items-center justify-end gap-2">
          {resetRequested && (
            <span className="text-[11px] text-[var(--co-muted)]">
              This will reset all price buckets to their defaults. Any custom configuration will be
              lost.
            </span>
          )}
          {dirty && <span className="text-[11px] text-[var(--co-muted)]">Unsaved changes</span>}
          <Button variant="ghost" onClick={handleDiscard} disabled={!dirty || saving}>
            Discard
          </Button>
          <Button onClick={() => void handleSave()} disabled={locked || !dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <PackHealthPanel
        validation={preview}
        loading={validating}
        currency={packCurrency}
        title="Preview"
      />
    </div>
  );
}
